import { Service, SalesPointItem } from '@turizi/shared';
import { useTranslation } from 'react-i18next';
import { Button, Loader, Select, Checkbox } from '@mantine/core';
import { IconExternalLink, IconCheck, IconX } from '@tabler/icons-react';
import { ServiceTitleStatic } from '../services/service-title';
import { useState, useMemo } from 'react';
import { useUpdateService } from '@/hooks/api-hooks';
import { Link } from 'react-router-dom';
import { SERVICES_PATH } from '@/lib/navigation';
import { localTenant } from '@/services/session-service';
import { getInitialSalesPoints } from '@/pages/settings/tax-settings';

interface Props {
    services: Service[];
}

interface ServiceUpdateStatus {
    id: Service['id'];
    loading: boolean;
    success: boolean | null;
}

const DELAY = 350; // ms

export function BulkEditTaxesTab({ services }: Props) {
    const { t } = useTranslation();
    const [modifyPos, setModifyPos] = useState<boolean>(false);
    const [selectedPos, setSelectedPos] = useState<string | null>(null);
    const [modifyTaxPercentage, setModifyTaxPercentage] = useState<boolean>(false);
    const [selectedTaxPercentage, setSelectedTaxPercentage] = useState<string | null>(null);
    const [updateStatuses, setUpdateStatuses] = useState<Record<Service['id'], ServiceUpdateStatus>>({});
    const [counter, setCounter] = useState<number>(0);

    const updateServiceMutation = useUpdateService();
    const tenant = localTenant();

    const afipSettings: any = tenant?.settings?.afip;
    const salesPoints = useMemo(() => getInitialSalesPoints(afipSettings), [afipSettings]);

    const salesPointsMap = useMemo(() => {
        const map = new Map<string, SalesPointItem>();
        salesPoints.forEach((sp) => {
            const numStr = String(sp.number ?? '').trim();
            if (numStr) {
                map.set(numStr, sp);
                const parsedNum = Number(numStr);
                if (!isNaN(parsedNum)) {
                    map.set(String(parsedNum), sp);
                }
            }
        });
        return map;
    }, [salesPoints]);

    const formatPosLabel = (pos: number | string | null | undefined) => {
        if (pos == null || pos === '') return t('none');
        const posStr = String(pos).trim();
        const sp = salesPointsMap.get(posStr);
        if (!sp) return `POS ${pos}`;

        const num = sp.number || posStr;
        const name = (sp.name || '').trim();
        const system = (sp.system || '').trim();

        if (name && system) {
            return `POS ${num} - ${name} (${system})`;
        }
        if (name) {
            return `POS ${num} - ${name}`;
        }
        return `POS ${num}`;
    };

    const posSelectData = useMemo(() => {
        const data = salesPoints.map((sp) => {
            const numStr = String(sp.number ?? '').trim();
            return {
                value: numStr,
                label: formatPosLabel(numStr),
            };
        }).filter((item) => item.value !== '' && item.value !== 'undefined' && item.value !== 'null');

        services.forEach(s => {
            if (s.pos != null && !data.some(p => p.value === String(s.pos))) {
                data.push({
                    value: String(s.pos),
                    label: formatPosLabel(s.pos)
                });
            }
        });

        return data;
    }, [salesPoints, services, salesPointsMap]);

    const taxOptions = [
        { value: '21', label: '21%' },
        { value: '10.5', label: '10.5%' },
        { value: '27', label: '27%' },
        { value: '0', label: '0% (Exento)' }
    ];

    const hasStarted = Object.keys(updateStatuses).length > 0;
    const allCompleted = hasStarted && Object.values(updateStatuses).every(status => !status.loading);

    const getUpdates = (): Partial<Service> => {
        const updates: Partial<Service> = {};
        if (modifyPos) {
            updates.pos = selectedPos !== null && selectedPos !== '' ? parseInt(selectedPos, 10) : null;
        }
        if (modifyTaxPercentage) {
            updates.tax_percentage = selectedTaxPercentage !== null && selectedTaxPercentage !== '' ? parseFloat(selectedTaxPercentage) : null;
        }
        return updates;
    };

    const handleBulkUpdate = async () => {
        if (!modifyPos && !modifyTaxPercentage) return;

        const initialStatuses: Record<Service['id'], ServiceUpdateStatus> = {};
        services.forEach(service => {
            initialStatuses[service.id] = { id: service.id, loading: true, success: null };
        });
        setUpdateStatuses(initialStatuses);
        setCounter(0);

        const updates = getUpdates();
        let completed = 0;
        for (let i = 0; i < services.length; i++) {
            const service = services[i];

            if (i > 0) {
                await new Promise(resolve => setTimeout(resolve, DELAY));
            }

            try {
                await updateServiceMutation.mutateAsync({
                    id: service.id,
                    ...updates,
                });

                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [service.id]: { id: service.id, loading: false, success: true }
                }));
            } catch {
                completed++;
                setCounter(completed);
                setUpdateStatuses(prev => ({
                    ...prev,
                    [service.id]: { id: service.id, loading: false, success: false }
                }));
            }
        }
    };

    const formatTaxLabel = (tax: number | null | undefined) => {
        if (tax == null) return t('none');
        return `${tax}%`;
    };

    return (
        <>
            <div className="space-y-4">
                <div className="text-xs text-gray-600">
                    {t('tax_settings_bulk_desc')}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* POS Card */}
                    <div className="solid-box-border space-y-3">
                        <Checkbox
                            label={t('modify_pos')}
                            checked={modifyPos}
                            onChange={(e) => setModifyPos(e.currentTarget.checked)}
                            color="black"
                        />
                        <Select
                            disabled={!modifyPos}
                            label={t('pos_label')}
                            description={t('pos_desc')}
                            placeholder={t('select_pos_placeholder')}
                            value={selectedPos}
                            onChange={(val) => setSelectedPos(val)}
                            data={posSelectData}
                            searchable
                            clearable
                        />
                    </div>

                    {/* Tax Percentage Card */}
                    <div className="solid-box-border space-y-3">
                        <Checkbox
                            label={t('modify_tax_percentage')}
                            checked={modifyTaxPercentage}
                            onChange={(e) => setModifyTaxPercentage(e.currentTarget.checked)}
                            color="black"
                        />
                        <Select
                            disabled={!modifyTaxPercentage}
                            label={t('tax_percentage')}
                            description={t('tax_percentage_desc')}
                            placeholder={t('select_tax_percentage')}
                            value={selectedTaxPercentage}
                            onChange={(val) => setSelectedTaxPercentage(val)}
                            data={taxOptions}
                            clearable
                        />
                    </div>
                </div>

                {/* Services list preview */}
                <div className="space-y-2 max-h-96 overflow-y-auto pr-1 mt-4">
                    {services?.map(service => (
                        <div
                            key={service.id}
                            className="p-2.5 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-between gap-3"
                        >
                            <div className="flex-1 min-w-0">
                                <Link
                                    to={`${SERVICES_PATH}/${service.id}`}
                                    target="_blank"
                                    className="text-xs font-medium text-gray-800 hover:text-blue-600 flex items-center gap-1.5 truncate"
                                >
                                    <IconExternalLink size={14} className="shrink-0 text-gray-400" />
                                    <span className="truncate">{ServiceTitleStatic(service)}</span>
                                </Link>
                                <div className="flex flex-wrap items-center gap-2 mt-1.5 text-xs text-gray-500">
                                    <span className="inline-flex items-center gap-1">
                                        <span className="text-gray-400">{t('pos_label')}:</span>
                                        <span className="font-semibold text-gray-700">{formatPosLabel(service.pos)}</span>
                                        {modifyPos && (
                                            <>
                                                <span className="text-gray-400 font-bold">→</span>
                                                <span className="font-bold text-blue-600">
                                                    {formatPosLabel(selectedPos !== null && selectedPos !== '' ? parseInt(selectedPos, 10) : null)}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                    <span className="text-gray-300">•</span>
                                    <span className="inline-flex items-center gap-1">
                                        <span className="text-gray-400">{t('tax_percentage')}:</span>
                                        <span className="font-semibold text-gray-700">{formatTaxLabel(service.tax_percentage)}</span>
                                        {modifyTaxPercentage && (
                                            <>
                                                <span className="text-gray-400 font-bold">→</span>
                                                <span className="font-bold text-teal-600">
                                                    {formatTaxLabel(selectedTaxPercentage !== null && selectedTaxPercentage !== '' ? parseFloat(selectedTaxPercentage) : null)}
                                                </span>
                                            </>
                                        )}
                                    </span>
                                </div>
                            </div>
                            <div className="shrink-0">
                                {updateStatuses[service.id]?.loading && <Loader size="xs" color="black" />}
                                {updateStatuses[service.id]?.success === true && <IconCheck size={16} className="text-green-600" />}
                                {updateStatuses[service.id]?.success === false && <IconX size={16} className="text-red-600" />}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="mt-8">
                {!hasStarted ? (
                    <div className="flex justify-end">
                        <Button
                            onClick={handleBulkUpdate}
                            disabled={!modifyPos && !modifyTaxPercentage}
                            color="black"
                            radius="xl"
                            size="xs"
                        >
                            {t('apply_changes')}
                        </Button>
                    </div>
                ) : allCompleted ? (
                    <div className="flex items-center justify-center gap-2">
                        <div className="text-sm font-medium text-green-600">
                            {t('done')}
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2">
                        <div className="text-sm font-medium">
                            {t('updating')} {counter} / {services.length}
                        </div>
                        <Loader size="sm" color="black" />
                    </div>
                )}
            </div>
        </>
    );
}
